import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { metrics } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import {
  AggregationTemporalityPreference,
  OTLPMetricExporter,
} from '@opentelemetry/exporter-metrics-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { logException, logMessage } from './otel-logger';

/**
 * Instrumentação OpenTelemetry do navegador → SigNoz (traces, logs e métricas/Web Vitals).
 *
 * Só liga em produção real e só com endpoint configurado — mesma regra do backend
 * (`SigNozStartup.cs`), onde staging e local não mandam nada.
 *
 * Substituiu o Sentry/GlitchTip (removido em 04/08/2026, não estava mais em uso): erro não
 * tratado virou log com atributos `exception.*` e o `browserTracingIntegration` virou os spans
 * de fetch/XHR/document-load daqui.
 *
 * O endpoint é um caminho RELATIVO (`/otlp`), servido pelo proxy same-origin do nginx
 * (`nginx-k8s.conf`). Isso não é preferência de estilo, é o que torna a coisa possível:
 * a porta 4318 do collector é HTTP puro (mixed content bloquearia), não tem bloco `cors:`,
 * e o firewall só autoriza o IP de saída do k3s — que é justamente quem faz o proxy.
 * Ver `docs/Infra/observabilidade/signoz-caprover/README.md`.
 */

export type VaultOtelEnvironment = {
  name: string;
  production: boolean;
  apiUrl: string;
  buildNumber?: string;
  signoz?: {
    endpoint?: string;
    serviceName?: string;
    captureInteractions?: boolean;
  };
};

/**
 * Endpoints de POLLING que não geram span.
 *
 * ── Por que existe ────────────────────────────────────────────────────────────────────────────
 * `notifications/current` é chamado de 5 em 5 segundos pelo `side-menu`, que é global: roda para
 * todo usuário autenticado, em qualquer tela, esteja ele fazendo algo ou não. São 12 spans/min
 * POR ABA, contínuos — medido em produção em 04/08/2026, era a maior fonte de spans da API,
 * empatada com o beacon do Clarity. O valor diagnóstico é ~zero: ninguém investiga a latência
 * individual de um heartbeat de 5s. É o mesmo raciocínio pelo qual esse polling JÁ é desligado em
 * LOCAL (ver comentário em `startNotificationsPolling`): em local ele "só polui a aba Rede".
 *
 * ── Por que a lista é tão curta (e por que NÃO é por cadência) ────────────────────────────────
 * A primeira ideia foi ignorar todo polling de cadência baixa (≤15s): padronização contábil 5s,
 * RFQ 10s, e-mails de RFQ 15s. **Está errado**, e o motivo importa: nesses casos o polling REUSA
 * a mesma URL da carga inicial da tela (`loadRfqs(false)`, `loadDocuments(true)`). Ignorar a URL
 * cegaria também o primeiro carregamento e o refresh que o usuário pede — perderíamos justamente
 * o span que interessa. Só entra aqui URL usada EXCLUSIVAMENTE por polling.
 *
 * `notifications/current` passa nesse teste: `getCurrent()` tem um único chamador, o próprio
 * polling. As ações do usuário usam caminhos distintos (`current/paged`, `current/read`,
 * `current/open`, `current/read-all`) e seguem traçadas — daí o `(?:[?#]|$)` no fim do padrão.
 *
 * ── Antes de acrescentar algo aqui ────────────────────────────────────────────────────────────
 * 1. Confirme que a URL é usada SÓ por polling (`grep` no método do serviço HTTP). Se a carga
 *    inicial usa a mesma URL, NÃO adicione — o lugar do corte passa a ser um processor `filter`
 *    no OTel Collector, que consegue descartar poll bem-sucedido e MANTER os com erro.
 * 2. Garanta que a falha do polling é visível por outro caminho. Muitos polls fazem
 *    `catchError(() => of([]))` e engolem o erro em silêncio: sem o span, ninguém saberia que
 *    pararam de funcionar. O do `side-menu` emite `logException` no catch exatamente por isso.
 */
export const POLLING_URL_PATTERNS: RegExp[] = [/\/api\/v\d+\/notifications\/current(?:[?#]|$)/i];

const VITALS_METER_NAME = 'vault-frontend.web-vitals';
const METRIC_EXPORT_INTERVAL_MS = 60_000;

/**
 * Web Vitals que são duração (ms). CLS é adimensional (layout shift score) e fica de fora.
 */
const VITALS_IN_MILLISECONDS = new Set(['LCP', 'FCP', 'INP', 'TTFB']);

/**
 * Decidido separado da inicialização para poder ser testado sem carregar o SDK inteiro.
 */
export function isOtelEnabled(environment: VaultOtelEnvironment): boolean {
  const endpoint = environment.signoz?.endpoint?.trim();
  const isRealProduction = environment.production && environment.name === 'PRODUCTION';

  return Boolean(isRealProduction && endpoint);
}

export function initOpenTelemetryIfEnabled(environment: VaultOtelEnvironment): void {
  if (!isOtelEnabled(environment)) {
    return;
  }

  const baseUrl = resolveBaseUrl(environment.signoz?.endpoint as string);
  const resource = buildResource(environment);

  // A instrumentação de fetch/XHR precisa IGNORAR o próprio endpoint de exportação. Sem isso,
  // cada export gera spans, que geram outro export — laço infinito que só cresce.
  const exporterUrlPattern = new RegExp(escapeForRegExp(baseUrl));

  initTraces(environment, resource, baseUrl, [
    exporterUrlPattern,
    buildForeignOriginPattern(environment.apiUrl),
    ...POLLING_URL_PATTERNS,
  ]);
  initLogs(resource, baseUrl);
  initMetricsAndWebVitals(resource, baseUrl);
}

function initTraces(
  environment: VaultOtelEnvironment,
  resource: ReturnType<typeof resourceFromAttributes>,
  baseUrl: string,
  ignoreUrls: RegExp[]
): void {
  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: `${baseUrl}/v1/traces` })),
    ],
  });

  // ZoneContextManager (e não o StackContextManager default) porque o app roda com zone.js:
  // é o que mantém o contexto do span atravessando async, então o span do fetch fica filho
  // do span da navegação em vez de virar um trace órfão.
  provider.register({ contextManager: new ZoneContextManager() });

  // Propaga `traceparent` SÓ para a API do Vault — nunca `/.*/`, que vazaria o header para
  // terceiros (Hotjar, Clarity, GA) e poderia estourar o CORS deles. A API já aceita o header:
  // o CORS do backend é AllowAnyHeader (`NativeInjectorBootStrapper.cs`), e o .NET lê W3C
  // trace context por default — é isso que costura o trace do navegador ao `vault-backend`.
  const apiOriginPattern = buildApiOriginPattern(environment.apiUrl);

  registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        ignoreUrls,
        propagateTraceHeaderCorsUrls: apiOriginPattern,
        // Limpa as entradas de resource timing já consumidas: o buffer do navegador é finito e,
        // num SPA de sessão longa, enche e passa a descartar timing silenciosamente.
        clearTimingResources: true,
      }),
      new XMLHttpRequestInstrumentation({
        ignoreUrls,
        propagateTraceHeaderCorsUrls: apiOriginPattern,
      }),
      // DESLIGADA por default. Com ela o span do clique vira a RAIZ do trace e o fetch vira
      // filho — então sampling por trace derrubaria justamente as chamadas de API que
      // interessam. Se um dia o volume precisar de corte, o lugar certo é um processor
      // `filter` no collector, não sampler aqui.
      ...(environment.signoz?.captureInteractions
        ? [new UserInteractionInstrumentation({ eventNames: ['click', 'input', 'submit'] })]
        : []),
    ],
  });

  registerFlushOnPageHide(() => provider.forceFlush());
}

function initLogs(resource: ReturnType<typeof resourceFromAttributes>, baseUrl: string): void {
  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor({ exporter: new OTLPLogExporter({ url: `${baseUrl}/v1/logs` }) }),
    ],
  });

  logs.setGlobalLoggerProvider(loggerProvider);

  // `error` pega exceção síncrona que escapou; `unhandledrejection` pega promise rejeitada sem
  // catch. Os dois são a rede de segurança para o que NÃO passa pelo ErrorHandler do Angular
  // (código fora da zone, listener nativo, script de terceiro).
  window.addEventListener('error', event => {
    logException(event.error ?? event.message, {
      'code.filepath': event.filename ?? '',
      'code.lineno': event.lineno ?? 0,
      'vault.error_source': 'window.error',
    });
  });

  window.addEventListener('unhandledrejection', event => {
    logException(event.reason ?? 'Unhandled promise rejection', {
      'vault.error_source': 'unhandledrejection',
    });
  });

  // Encadeia o console.error preservando o original. A guarda `isEmitting` existe porque um erro
  // DENTRO do emit cairia no console.error de novo — recursão infinita no primeiro hiccup do SDK.
  const originalConsoleError = console.error.bind(console);
  let isEmitting = false;

  console.error = (...args: unknown[]): void => {
    originalConsoleError(...args);

    if (isEmitting) {
      return;
    }

    isEmitting = true;

    try {
      logMessage(args.map(stringifyLogArgument).join(' '), SeverityNumber.ERROR, {
        'vault.error_source': 'console.error',
      });
    } catch {
      // Observabilidade nunca pode derrubar a aplicação.
    } finally {
      isEmitting = false;
    }
  };

  registerFlushOnPageHide(() => loggerProvider.forceFlush());
}

function initMetricsAndWebVitals(
  resource: ReturnType<typeof resourceFromAttributes>,
  baseUrl: string
): void {
  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({
          url: `${baseUrl}/v1/metrics`,
          // O SigNoz espera temporalidade DELTA (é o que o `signozspanmetrics/delta` do collector
          // já usa). Com CUMULATIVE os histogramas aparecem, mas os painéis somam errado.
          temporalityPreference: AggregationTemporalityPreference.DELTA,
        }),
        exportIntervalMillis: METRIC_EXPORT_INTERVAL_MS,
      }),
    ],
  });

  metrics.setGlobalMeterProvider(meterProvider);

  const meter = metrics.getMeter(VITALS_METER_NAME);

  // Um histograma por vital (e não um único com label) para que cada um tenha sua unidade e
  // seus buckets no SigNoz — LCP em segundos e CLS adimensional não convivem no mesmo bucket.
  const histograms = new Map<string, ReturnType<typeof meter.createHistogram>>();

  const recordVital = (metric: Metric): void => {
    const isDuration = VITALS_IN_MILLISECONDS.has(metric.name);
    const key = metric.name.toLowerCase();

    if (!histograms.has(key)) {
      histograms.set(
        key,
        meter.createHistogram(`web_vitals.${key}`, {
          description: `Core Web Vital ${metric.name}`,
          unit: isDuration ? 'ms' : '1',
        })
      );
    }

    histograms.get(key)?.record(metric.value, {
      // `rating` (good/needs-improvement/poor) vem do próprio web-vitals com os thresholds
      // oficiais do Google — evita recalcular limiar na query do dashboard.
      rating: metric.rating,
      navigation_type: metric.navigationType,
      'url.path': window.location.pathname,
    });
  };

  onLCP(recordVital);
  onINP(recordVital);
  onCLS(recordVital);
  onTTFB(recordVital);
  onFCP(recordVital);

  registerFlushOnPageHide(() => meterProvider.forceFlush());
}

function buildResource(environment: VaultOtelEnvironment) {
  return resourceFromAttributes({
    [ATTR_SERVICE_NAME]: environment.signoz?.serviceName?.trim() || 'vault-frontend',
    [ATTR_SERVICE_VERSION]: environment.buildNumber ?? 'unknown',
    // `deployment.environment` (chave estável, não a `deployment.environment.name` do incubating)
    // porque é exatamente a que o backend emite — é o que permite filtrar front e API juntos.
    'deployment.environment': 'production',
    'browser.language': navigator.language,
    'browser.user_agent': navigator.userAgent,
    // Agrupa toda a telemetria de uma aba. Sem isso, dois usuários com o mesmo sintoma viram
    // um borrão único no Logs Explorer.
    'session.id': createSessionId(),
  });
}

/**
 * Aceita caminho relativo (`/otlp`, o caso normal — proxy same-origin) ou URL absoluta,
 * e devolve sempre uma base absoluta sem barra final.
 */
function resolveBaseUrl(endpoint: string): string {
  const trimmed = endpoint.trim().replace(/\/+$/, '');

  return new URL(trimmed, window.location.origin).toString().replace(/\/+$/, '');
}

/**
 * Padrão de URLs que NÃO devem gerar span: tudo que é absoluto e aponta para um host que não é
 * o nosso nem o da API.
 *
 * É allowlist (nega o resto) e não blocklist de fornecedor de propósito. Medido em produção em
 * 04/08/2026: uma aba PARADA gerava ~7 spans/minuto só de beacon do Microsoft Clarity
 * (`x.clarity.ms/collect`), afogando os poucos spans de API que realmente importam — com
 * retenção de 15 dias num ClickHouse de VM modesta. Uma lista de fornecedores resolveria hoje e
 * apodreceria no próximo script de marketing que alguém adicionar ao index.html; negar todo
 * cross-origin desconhecido se mantém correta sozinha.
 *
 * Consequência aceita: chamada legítima a terceiro (ex.: download no blob storage) deixa de ser
 * traçada. Para um SPA, o que interessa é a conversa com a própria API.
 *
 * Detalhes: URL relativa (`/api/...`) não casa e continua traçada, porque é same-origin; e o
 * guarda `(?:[:/?#]|$)` impede bypass por prefixo — `app.cognitobrm.com.evil.com` é ignorado.
 * Coberto por `otel-init.spec.ts`.
 */
export function buildForeignOriginPattern(apiUrl: string, ownHost: string = getOwnHost()): RegExp {
  const hosts = [ownHost, safeHost(apiUrl)].filter(Boolean).map(escapeForRegExp);

  if (hosts.length === 0) {
    // Sem host conhecido, não ignora nada: perder span é melhor que perder tudo em silêncio.
    return /(?!)/;
  }

  return new RegExp(`^https?://(?!(?:${hosts.join('|')})(?:[:/?#]|$))`, 'i');
}

function getOwnHost(): string {
  return typeof window === 'undefined' ? '' : window.location.host;
}

function safeHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

/**
 * Restringe a propagação do `traceparent` à origem da API. Se o apiUrl vier malformado,
 * devolve um padrão que não casa com nada — melhor não propagar do que propagar para todos.
 */
function buildApiOriginPattern(apiUrl: string): RegExp {
  try {
    return new RegExp(`^${escapeForRegExp(new URL(apiUrl).origin)}`);
  } catch {
    return /(?!)/;
  }
}

/**
 * O BatchSpanProcessor guarda telemetria em memória até o intervalo de export. Numa navegação
 * para fora (ou fechar a aba) o lote pendente morre com a página — e é justo o lote que contém
 * o erro que fez o usuário sair. `visibilitychange`/`pagehide` são os únicos eventos confiáveis
 * para isso hoje (`unload` não dispara em mobile/back-forward cache).
 */
function registerFlushOnPageHide(flush: () => Promise<void>): void {
  const safeFlush = (): void => {
    void flush().catch(() => {
      // Nada a fazer: a página está indo embora.
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      safeFlush();
    }
  });

  window.addEventListener('pagehide', safeFlush);
}

function createSessionId(): string {
  return crypto.randomUUID();
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stringifyLogArgument(argument: unknown): string {
  if (typeof argument === 'string') {
    return argument;
  }

  if (argument instanceof Error) {
    return `${argument.name}: ${argument.message}`;
  }

  try {
    return JSON.stringify(argument);
  } catch {
    return String(argument);
  }
}
